<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { Sparkles, X } from '@lucide/vue'

interface AgentChatAction {
  code: string
  name: string
  status: string
  description: string
  arguments: Record<string, unknown>
  result: unknown
}

interface AgentChatResponse {
  conversationId: string
  status: string
  intent: string
  toolCode: string
  answer: string
  data: unknown
  actions: AgentChatAction[]
  followUps: string[]
  context: Record<string, unknown>
}

interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  text: string
  actions?: AgentChatAction[]
  followUps?: string[]
  data?: unknown
}

const props = withDefaults(
  defineProps<{
    title: string
    subtitle?: string
    placeholder?: string
    examples?: string[]
    context?: Record<string, unknown>
    modeLabel?: string
    disabled?: boolean
    send: (message: string, context: Record<string, unknown>) => Promise<AgentChatResponse>
  }>(),
  {
    subtitle: '控制面助手',
    placeholder: '输入要执行的管理动作',
    examples: () => [],
    context: () => ({}),
    modeLabel: '',
    disabled: false
  }
)

const emit = defineEmits<{
  response: [response: AgentChatResponse]
  error: [message: string]
  action: [action: AgentChatAction]
}>()

const open = ref(false)
const prompt = ref('')
const sending = ref(false)
const error = ref('')
const messageListRef = ref<HTMLElement | null>(null)
const messages = ref<ChatMessage[]>([
  {
    id: 'welcome',
    role: 'system',
    text: 'AI Agent 已连接。'
  }
])

const canSend = computed(() => prompt.value.trim().length > 0 && !sending.value && !props.disabled)

function showPanel() {
  if (props.disabled) return
  open.value = true
  void scrollToBottom()
}

function closePanel() {
  open.value = false
}

function useExample(example: string) {
  prompt.value = example
  open.value = true
}

async function sendFollowUp(followUp: string) {
  if (sending.value) return
  prompt.value = followUp
  await sendPrompt()
}

function dataSummary(message: ChatMessage): string | null {
  if (!message.data) return null
  if (typeof message.data !== 'object' || message.data === null) return null
  const data = message.data as Record<string, unknown>
  const summary = data.summary
  return typeof summary === 'string' && summary.trim() ? summary.trim() : null
}

// 从 data.data 提取档案关键字段渲染成 key-value 列表
const defaultFieldDefs: Array<{ key: string; label: string }> = [
  { key: 'name', label: '姓名' },
  { key: 'title', label: '标题/职位' },
  { key: 'code', label: '编码' },
  { key: 'status', label: '状态' },
  { key: 'statusText', label: '状态' },
  { key: 'mobile', label: '手机号' },
  { key: 'email', label: '邮箱' },
  { key: 'type', label: '类型' },
  { key: 'category', label: '分类' }
]

function dataFields(message: ChatMessage): Array<{ label: string; value: string }> {
  if (!message.data) return []
  if (typeof message.data !== 'object' || message.data === null) return []
  const outer = message.data as Record<string, unknown>
  const inner = outer.data ?? outer.profile ?? outer
  if (!isRecord(inner)) return []
  const record = inner as Record<string, unknown>
  const fieldDefs = (props.context?.fieldDefs as Array<{ key: string; label: string }>) || defaultFieldDefs
  const result: Array<{ label: string; value: string }> = []
  for (const def of fieldDefs) {
    const raw = record[def.key]
    if (raw === null || raw === undefined) continue
    const text = String(raw).trim()
    if (!text) continue
    result.push({ label: def.label, value: text })
  }
  return result
}

function messageClass(message: ChatMessage) {
  return {
    'agent-chat__message--user': message.role === 'user',
    'agent-chat__message--assistant': message.role === 'assistant',
    'agent-chat__message--system': message.role === 'system'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function actionSummary(action: AgentChatAction) {
  return [action.name, statusText(action.status), actionBrief(action)].filter(Boolean).join(' · ')
}

function actionBrief(action: AgentChatAction) {
  const summary = actionToolSummary(action)
  if (summary) return summary
  const target = actionTarget(action)
  if (target?.path && typeof target.path === 'string') return `目标 ${target.path}`
  if (action.description && action.description !== '已执行') return action.description
  return ''
}

function statusText(status: string) {
  const normalized = status.toUpperCase()
  if (normalized === 'DONE') return '已完成'
  if (normalized === 'SUGGESTED') return '可打开'
  if (normalized === 'NEED_INPUT') return '待补充'
  if (normalized === 'FAILED') return '失败'
  return status
}

function actionToolSummary(action: AgentChatAction) {
  return (
    summaryFrom(action.result) ??
    summaryFrom(action.arguments) ??
    null
  )
}

function messageToolSummaries(message: ChatMessage) {
  const summaries = (message.actions ?? [])
    .map((action) => actionToolSummary(action))
    .filter((summary): summary is string => Boolean(summary))
  return Array.from(new Set(summaries))
}

function summaryFrom(value: unknown): string | null {
  if (!isRecord(value)) return null
  const summary = stringField(value, 'summary') ?? stringField(value, 'toolSummary')
  if (summary) return summary
  const result = value.result
  if (isRecord(result)) {
    return stringField(result, 'summary') ?? stringField(result, 'toolSummary')
  }
  return null
}

function stringField(record: Record<string, unknown>, key: string) {
  const value = record[key]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function actionTarget(action: AgentChatAction) {
  if (!isNavigationAction(action)) return null
  return (
    navigationTargetFrom(action.result) ??
    navigationTargetFrom(action.arguments) ??
    nestedNavigationTarget(action.result, 'targetMenu') ??
    nestedNavigationTarget(action.arguments, 'targetMenu') ??
    null
  )
}

function isNavigationAction(action: AgentChatAction) {
  const code = action.code.toLowerCase()
  if (code.includes('.query')) return false
  return (
    code.includes('.open') ||
    code.includes('navigation') ||
    action.name.includes('打开') ||
    action.name.includes('导航') ||
    navigationTargetFrom(action.result) !== null ||
    navigationTargetFrom(action.arguments) !== null
  )
}

function navigationTargetFrom(value: unknown) {
  if (!isRecord(value)) return null
  if (
    typeof value.moduleCode === 'string' ||
    typeof value.menuCode === 'string' ||
    typeof value.path === 'string' ||
    typeof value.tab === 'string' ||
    typeof value.permissionSection === 'string' ||
    typeof value.tenantId === 'number' ||
    typeof value.tenantId === 'string'
  ) {
    return value
  }
  return null
}

function nestedNavigationTarget(value: unknown, key: string) {
  if (!isRecord(value)) return null
  return navigationTargetFrom(value[key])
}

function canOpenAction(action: AgentChatAction) {
  return actionTarget(action) !== null
}

function actionClass(action: AgentChatAction) {
  return {
    'agent-chat__action--clickable': canOpenAction(action),
    'agent-chat__action--done': action.status.toUpperCase() === 'DONE',
    'agent-chat__action--pending': action.status.toUpperCase() === 'NEED_INPUT'
  }
}

function openAction(action: AgentChatAction) {
  if (!canOpenAction(action)) return
  emit('action', action)
}

async function sendPrompt() {
  const text = prompt.value.trim()
  if (!text || sending.value) return

  prompt.value = ''
  error.value = ''
  sending.value = true
  messages.value = [
    ...messages.value,
    {
      id: `user-${Date.now()}`,
      role: 'user',
      text
    }
  ]
  await scrollToBottom()

  try {
    const response = await props.send(text, props.context)
    messages.value = [
      ...messages.value,
      {
        id: response.conversationId || `assistant-${Date.now()}`,
        role: 'assistant',
        text: response.answer || '已处理。',
        actions: response.actions,
        followUps: response.followUps,
        data: response.data
      }
    ]
    emit('response', response)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'AI Agent 调用失败'
    error.value = message
    messages.value = [
      ...messages.value,
      {
        id: `error-${Date.now()}`,
        role: 'assistant',
        text: message
      }
    ]
    emit('error', message)
  } finally {
    sending.value = false
    await scrollToBottom()
  }
}

async function scrollToBottom() {
  await nextTick()
  messageListRef.value?.scrollTo({
    top: messageListRef.value.scrollHeight,
    behavior: 'smooth'
  })
}
</script>

<template>
  <button
    type="button"
    class="agent-chat-trigger"
    :disabled="disabled"
    title="AI Agent"
    @click="showPanel"
  >
    <Sparkles :size="15" :stroke-width="1.8" aria-hidden="true" />
    <span>AI Agent</span>
  </button>

  <Teleport to="body">
    <div v-if="open" class="agent-chat" role="dialog" aria-modal="true" :aria-label="title">
      <button type="button" class="agent-chat__scrim" aria-label="关闭 AI Agent" @click="closePanel"></button>
      <section class="agent-chat__panel">
        <header class="agent-chat__header">
          <div>
            <p>{{ subtitle }}</p>
            <h2>{{ title }}</h2>
          </div>
          <span v-if="modeLabel" class="agent-chat__mode">{{ modeLabel }}</span>
          <button type="button" class="agent-chat__close" aria-label="关闭" @click="closePanel">
            <X :size="18" :stroke-width="1.8" aria-hidden="true" />
          </button>
        </header>

        <div ref="messageListRef" class="agent-chat__messages">
          <article
            v-for="message in messages"
            :key="message.id"
            class="agent-chat__message"
            :class="messageClass(message)"
          >
            <p>{{ message.text }}</p>
            <p v-if="dataSummary(message)" class="agent-chat__data-summary">{{ dataSummary(message) }}</p>
            <dl v-if="dataFields(message).length" class="agent-chat__data-fields">
              <div v-for="field in dataFields(message)" :key="field.label" class="agent-chat__data-field">
                <dt>{{ field.label }}</dt>
                <dd>{{ field.value }}</dd>
              </div>
            </dl>
            <div v-if="message.actions?.length" class="agent-chat__actions">
              <button
                v-for="action in message.actions"
                :key="`${message.id}-${action.code}-${action.name}`"
                type="button"
                :class="actionClass(action)"
                :disabled="!canOpenAction(action)"
                @click="openAction(action)"
              >
                {{ actionSummary(action) }}
              </button>
            </div>
            <div v-if="messageToolSummaries(message).length" class="agent-chat__tool-summaries">
              <p
                v-for="summary in messageToolSummaries(message)"
                :key="summary"
              >
                {{ summary }}
              </p>
            </div>
            <div v-if="message.followUps?.length" class="agent-chat__followups">
              <button
                v-for="followUp in message.followUps"
                :key="followUp"
                type="button"
                :disabled="sending"
                @click="sendFollowUp(followUp)"
              >
                {{ followUp }}
              </button>
            </div>
          </article>
        </div>

        <div v-if="examples.length" class="agent-chat__examples">
          <button v-for="example in examples" :key="example" type="button" @click="useExample(example)">
            {{ example }}
          </button>
        </div>

        <p v-if="error" class="agent-chat__error">{{ error }}</p>

        <form class="agent-chat__composer" @submit.prevent="sendPrompt">
          <textarea
            v-model="prompt"
            :placeholder="placeholder"
            rows="3"
            @keydown.enter.exact.prevent="sendPrompt"
          ></textarea>
          <button type="submit" :disabled="!canSend">
            {{ sending ? '执行中' : '发送' }}
          </button>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.agent-chat-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  border: 1px solid var(--rr-color-border);
  border-radius: 7px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  padding: 0 10px;
}

.agent-chat-trigger:hover {
  border-color: var(--rr-color-primary);
  color: var(--rr-color-primary);
}

.agent-chat-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.agent-chat {
  position: fixed;
  z-index: 900;
  inset: 0;
}

.agent-chat__scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgb(15 23 42 / 24%);
  cursor: pointer;
}

.agent-chat__panel {
  position: absolute;
  top: 0;
  right: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto auto auto;
  width: min(440px, 100vw);
  height: 100vh;
  box-sizing: border-box;
  border-left: 1px solid var(--rr-color-border);
  background: var(--rr-color-canvas);
  box-shadow: -18px 0 44px rgb(15 23 42 / 14%);
}

.agent-chat__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--rr-color-border);
  background: #ffffff;
  padding: 16px;
}

.agent-chat__header p,
.agent-chat__header h2,
.agent-chat__message p,
.agent-chat__error {
  margin: 0;
}

.agent-chat__header p {
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.agent-chat__header h2 {
  color: var(--rr-color-text-strong);
  font-size: 17px;
  line-height: 1.25;
}

.agent-chat__mode {
  border: 1px solid var(--rr-color-primary-border);
  border-radius: 999px;
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
  font-size: 12px;
  font-weight: 900;
  padding: 5px 8px;
  white-space: nowrap;
}

.agent-chat__close {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--rr-color-text-muted);
  cursor: pointer;
}

.agent-chat__close:hover {
  background: var(--rr-color-surface-subtle);
}

.agent-chat__messages {
  display: grid;
  align-content: start;
  gap: 10px;
  overflow: auto;
  padding: 14px;
}

.agent-chat__message {
  display: grid;
  gap: 8px;
  max-width: 92%;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: #ffffff;
  color: var(--rr-color-text);
  font-size: 14px;
  line-height: 1.55;
  padding: 10px 12px;
}

.agent-chat__message--user {
  justify-self: end;
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
}

.agent-chat__message--system {
  justify-self: center;
  max-width: 100%;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.agent-chat__actions,
.agent-chat__followups,
.agent-chat__examples {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.agent-chat__actions button {
  border: 0;
  border-radius: 4px;
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 6px;
  text-align: left;
}

.agent-chat__actions button.agent-chat__action--clickable:hover:not(:disabled) {
  background: var(--rr-color-primary-soft);
}

.agent-chat__actions button:disabled {
  cursor: default;
  opacity: 0.82;
}

.agent-chat__actions button.agent-chat__action--done:not(.agent-chat__action--clickable) {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
}

.agent-chat__actions button.agent-chat__action--pending {
  background: var(--rr-color-warning-soft);
  color: var(--rr-color-warning);
}

.agent-chat__data-summary {
  margin: 4px 0 0;
  padding: 6px 10px;
  font-size: 13px;
  color: var(--rr-color-text, inherit);
  background: var(--rr-color-bg-subtle, transparent);
  border-radius: 6px;
}

.agent-chat__tool-summaries {
  display: grid;
  gap: 4px;
  border-left: 3px solid var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary-hover);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  padding: 7px 9px;
}

.agent-chat__tool-summaries p {
  margin: 0;
}

.agent-chat__followups button,
.agent-chat__examples button {
  border: 1px solid var(--rr-color-border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--rr-color-text);
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 8px;
}

.agent-chat__followups button:hover,
.agent-chat__examples button:hover {
  border-color: var(--rr-color-primary);
  color: var(--rr-color-primary);
}

.agent-chat__examples {
  border-top: 1px solid var(--rr-color-border);
  padding: 10px 14px 0;
}

.agent-chat__error {
  color: var(--rr-color-danger);
  font-size: 13px;
  font-weight: 700;
  padding: 0 14px;
}

.agent-chat__composer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 76px;
  gap: 10px;
  border-top: 1px solid var(--rr-color-border);
  background: #ffffff;
  padding: 12px;
}

.agent-chat__composer textarea {
  width: 100%;
  min-height: 72px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 6px;
  color: var(--rr-color-text);
  resize: none;
  padding: 10px;
}

.agent-chat__composer button {
  border: 0;
  border-radius: 6px;
  background: var(--rr-color-primary);
  color: #ffffff;
  cursor: pointer;
  font-weight: 900;
}

.agent-chat__composer button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

@media (max-width: 640px) {
  .agent-chat__panel {
    width: 100vw;
  }
}
</style>
